import * as Yup from "yup";
import { t } from "i18next";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import { jwtDecode } from "jwt-decode";
import Button from "@mui/material/Button";
import PhoneInput from "react-phone-input-2";
import Snackbar from "@mui/material/Snackbar";
import Checkbox from "@mui/material/Checkbox";
import { useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import { GoogleLogin } from "@react-oauth/google";
import { RHFPhoneInputProps } from "./SignUp.props";
import BookingApi from "../../../API/Apis/BookingApi";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { formatTheme } from "../../../config/MuiColorThemes";
import MyReservationsPage from "../../../views/reservations";
import FormControlLabel from "@mui/material/FormControlLabel";
import { CircularProgress, FormControl } from "@mui/material";
import { forwardRef, useEffect, useRef, useState } from "react";
import NotAuthorisedPage from "../../../views/notAuthorisedPage";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AppSettingResponse } from "../../../@types/DTO/AppSetting";
import { Divider, FormHelperText, Typography } from "@mui/material";
import { BookingGetters } from "../../../store/onlineBooking/getters";
import { useBackgroundLoader } from "../../UI/BgReservedArea/BackgroundLoader";

export default function SignUp() {
  const navigate = useNavigate();
  const dynamicPalette = formatTheme();
  const dynamicTheme = createTheme(dynamicPalette);

  const [step, setStep] = useState<number>(1);
  const [errorToast, setErrorToast] = useState({
    open: false,
    message: "",
    type: "",
  });
  const phoneRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const appSettings = BookingGetters.getAppSettings();

  const [showReserve, setShowReserve] = useState<boolean>(
    !!appSettings?.reservedArea
  );

  const ageValidation = new Date();
  ageValidation.setFullYear(ageValidation.getFullYear() - 18);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(t("First Name is required")),
    lastName: Yup.string().required(t("Last Name is required")),
    phoneNumber: Yup.string()
      .required(t("Phone Number is required"))
      .min(10, t("Phone Number must be at least 10 digits"))
      .max(15, t("Phone Number must be at most 15 digits")),
    email: Yup.string()
      .required(t("Email is required"))
      .email(t("Email is invalid")),
    password: Yup.string()
      .required(t("Password is required"))
      .min(6, t("Password must be at least 6 characters")),
    confirmPassword: Yup.string()
      .required(t("Confirm Password is required"))
      .oneOf([Yup.ref("password"), null], t("Confirm Password does not match")),

    birthDate: Yup.date()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .nullable()
      .required(t("Birth Date is required"))
      .max(ageValidation, t("Age must be at least 18 years old")),

    acceptTerms: Yup.bool().oneOf([true], t("Accept Terms is required")),
  });

  const reservedAreaBgImageUrl = appSettings?.config?.reservedAreaBgImage;
  const defaultImage = "/assets/images/reserve_area_car.jpg";
  const { getImage, backgroundStyle } = useBackgroundLoader({
    imageUrl: reservedAreaBgImageUrl,
    defaultImage,
  });

  const {
    register,
    handleSubmit,
    trigger,
    control,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    shouldUnregister: false,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      birthDate: null,
      acceptTerms: false,
    },
  });

  const nextStep = async (): Promise<void> => {
    if (step === 1) {
      const valid = await trigger(["firstName", "lastName", "phoneNumber"]);
      if (valid) {
        clearErrors(["email", "password", "confirmPassword"]);
        setStep(2);
      }
    } else if (step === 2) {
      const valid = await trigger(["email", "password", "confirmPassword"]);
      if (valid) {
        clearErrors(["birthDate", "acceptTerms"]);
        setStep(3);
      }
    }
  };

  const onSubmit = async (formData: any): Promise<void> => {
    if (step !== 3) return;

    const isValid = await trigger(["birthDate", "acceptTerms"]);
    if (!isValid) return;

    try {
      setLoader(true);

      const response = await BookingApi.createNewUser(formData);
      const result = response.data;
      <MyReservationsPage ids={result.id} />;
      if (result.status) {
        localStorage.setItem("userID", result.id);
        localStorage.setItem("isUserLoggedin", "true");

        navigate("/SignUpSuccess");
      } else {
        setErrorToast({
          open: true,
          message: result.message,
          type: "error",
        });

        if (result.message === "Email already exist") {
          setError("email", {
            type: "manual",
            message: t("Email already exist"),
          });
          setStep(2);
        }

        if (result.message === "Mobile number already exist") {
          setError("phoneNumber", {
            type: "manual",
            message: t("Phone Number already exist"),
          });
          setStep(1);
        }
      }
    } catch (error) {
      console.error(error);
      setErrorToast({
        open: true,
        message: "Something went wrong! Try again.",
        type: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    BookingApi.getAppSetting().then((e: AppSettingResponse) => {
      setShowReserve(e.reservedArea);
    });
  }, []);

  const handleGoogleSuccess = async (
    credentialResponse: any
  ): Promise<void> => {
    try {
      setLoader(true);

      const decoded: any = jwtDecode(credentialResponse.credential);
      const googleUserData = {
        firstName: decoded.given_name || "",
        lastName: decoded.family_name || "",
        email: decoded.email?.toLowerCase(),
        phoneNumber: "",
        password: decoded.sub,
        birthDate: "",
        onlineUserType: 3,
        onlineUserRole: 2,
        onlineUserStatus: 1,
      };

      const response = await BookingApi.createNewUser(googleUserData);
      <MyReservationsPage ids={response.data.id} />;

      if (response.data.status) {
        localStorage.setItem("userID", response.data.id);
        localStorage.setItem("isUserLoggedin", "true");
        navigate("/SignUpSuccess");
      } else {
        setErrorToast({
          open: true,
          message: response.data.message,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Google Sign-In error:", error);
      setErrorToast({
        open: true,
        message: "Google Sign-In failed. Please try again.",
        type: "error",
      });
    } finally {
      setLoader(false);
    }
  };
  // eslint-disable-next-line react/display-name
  const RHFPhoneInput = forwardRef<any, RHFPhoneInputProps>(
    ({ value, onChange, onBlur, country, ...props }, ref) => (
      <PhoneInput
        {...props}
        country={country}
        specialLabel={
          <span>
            {t("Phone Number")}
            <span style={{ color: "red" }}> *</span>
          </span>
        }
        onFocus={() => setFocused(true)}
        value={value}
        onChange={(
          val: string,
          data: any,
          event: any,
          formattedValue: string
        ) => onChange?.(val, data, event, formattedValue)}
        onBlur={onBlur}
        inputProps={{ ref }}
        style={{
          color: props.error
            ? "#d32f2f"
            : focused
            ? dynamicTheme.palette.primary.main
            : "#736969",
        }}
        dropdownClass="mx-4"
        inputStyle={{
          height: "100%",
          width: "100%",
          color: "black",
          borderWidth: "2px",
          borderColor: props.error
            ? "#d32f2f"
            : focused
            ? dynamicTheme.palette.primary.main
            : "#ccc",
        }}
      />
    )
  );

  return (
    <ThemeProvider theme={dynamicTheme}>
      {loader && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <CircularProgress sx={{ color: "white" }} />
        </div>
      )}
      <div className="relative min-h-screen flex items-center justify-center bg-transparent">
        {getImage && (
          <div
            style={backgroundStyle}
            className="absolute inset-0 w-full h-full object-cover lg:hidden z-0"
          />
        )}
        {showReserve ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 w-full min-h-screen">
            {/*  LEFT SECTION  */}
            <div className="hidden lg:block w-full h-screen overflow-hidden">
              {!getImage ? (
                <div className="absolute top-0 left-0 w-1/2 h-full flex items-center justify-center z-10">
                  <CircularProgress sx={{ color: "gray" }} />
                </div>
              ) : (
                <div className="w-full h-full object-cover">
                  <div
                    style={backgroundStyle}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>

            {/*  RIGHT SECTION  */}
            <div className="relative z-10 flex justify-center items-center max-h-screen lg:bg-gray-100">
              <div className="bg-white shadow-lg rounded-2xl px-9 py-11 w-full max-w-[22rem] md:max-w-[25rem] mx-4 border-2">
                <h1
                  className="text-center text-2xl font-semibold mb-6"
                  style={{ color: dynamicTheme?.palette["primary"]?.main }}
                >
                  {step === 1
                    ? t("Create your account")
                    : step === 2
                    ? t("Set your login details")
                    : step === 3
                    ? t("Final step")
                    : ""}
                </h1>

                {step === 3 && (
                  <div
                    className="text-center text-[#000000] mt-4 mb-4"
                    style={{ fontFamily: "sans-serif" }}
                  >
                    {t(
                      "Confirm your details and accept the Terms & Conditions to finish."
                    )}
                  </div>
                )}
                <Container component="main" disableGutters>
                  <Grid container justifyContent="center">
                    {/* FORM */}
                    <Box
                      component="form"
                      onSubmit={handleSubmit(onSubmit)}
                      noValidate
                      sx={{
                        width: "100%",
                        "& .MuiTextField-root": {
                          mt: 2,
                          width: "100%",
                          backgroundColor: "white",
                        },
                      }}
                    >
                      {/*  STEP 1 */}
                      {step === 1 && (
                        <>
                          <TextField
                            required
                            label={t("First Name")}
                            {...register("firstName")}
                            error={!!errors.firstName}
                            helperText={errors.firstName?.message}
                            FormHelperTextProps={{
                              sx: {
                                textAlign: "left",
                                ml: 0,
                              },
                            }}
                          />

                          <TextField
                            required
                            label={t("Last Name")}
                            {...register("lastName")}
                            error={!!errors.lastName}
                            helperText={errors.lastName?.message}
                            FormHelperTextProps={{
                              sx: {
                                textAlign: "left",
                                ml: 0,
                              },
                            }}
                          />
                          <div className="mt-5">
                            <Controller
                              name="phoneNumber"
                              control={control}
                              rules={{
                                required: t("Phone Number is required"),
                              }}
                              render={({ field, fieldState: { error } }) => (
                                <>
                                  <RHFPhoneInput
                                    {...field}
                                    ref={phoneRef}
                                    country="it"
                                    error={!!error}
                                  />
                                  {error && (
                                    <Typography variant="caption" color="error">
                                      {error.message}
                                    </Typography>
                                  )}
                                </>
                              )}
                            />
                          </div>

                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{
                              mt: 3,
                              mb: 2,
                              fontWeight: "bold",
                              fontSize: "17px",
                              p: 1,
                              textTransform: "none",
                            }}
                            onClick={nextStep}
                          >
                            {t("Next")}
                          </Button>
                        </>
                      )}

                      {/*  STEP 2 */}
                      {step === 2 && (
                        <>
                          <TextField
                            required
                            label={t("Email_Address")}
                            {...register("email")}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            FormHelperTextProps={{
                              sx: {
                                textAlign: "left",
                                ml: 0,
                              },
                            }}
                          />

                          <TextField
                            required
                            label={t("Password")}
                            type="password"
                            {...register("password")}
                            error={!!errors.password}
                            helperText={errors.password?.message}
                            FormHelperTextProps={{
                              sx: {
                                textAlign: "left",
                                ml: 0,
                              },
                            }}
                          />

                          <TextField
                            required
                            label={t("Confirm Password")}
                            type="password"
                            {...register("confirmPassword")}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword?.message}
                            FormHelperTextProps={{
                              sx: {
                                textAlign: "left",
                                ml: 0,
                              },
                            }}
                          />

                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{
                              mt: 3,
                              mb: 2,
                              fontWeight: "bold",
                              fontSize: "17px",
                              p: 1,
                              textTransform: "none",
                            }}
                            onClick={nextStep}
                          >
                            {t("Next")}
                          </Button>
                        </>
                      )}

                      {/*  STEP 3 */}
                      {step === 3 && (
                        <>
                          <TextField
                            required
                            id="birthDate"
                            label={t("Date of birth")}
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            {...register("birthDate")}
                            error={!!errors.birthDate}
                            helperText={errors.birthDate?.message}
                            FormHelperTextProps={{
                              sx: {
                                textAlign: "left",
                                ml: 0,
                              },
                            }}
                          />

                          <FormControl
                            error={!!errors.acceptTerms}
                            sx={{ mt: "18px", alignItems: "flex-start" }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...register("acceptTerms")}
                                  checked={!!watch("acceptTerms")}
                                />
                              }
                              label={
                                t("I accept the ") + t("Terms & Conditions")
                              }
                            />
                            {errors.acceptTerms && (
                              <FormHelperText
                                sx={{
                                  mt: 0,
                                  ml: 0,
                                  textAlign: "left",
                                  width: "100%",
                                }}
                              >
                                {errors.acceptTerms.message}
                              </FormHelperText>
                            )}
                          </FormControl>

                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{
                              mt: 3,
                              mb: 2,
                              fontWeight: "bold",
                              fontSize: "17px",
                              p: 1,
                              textTransform: "none",
                            }}
                            onClick={handleSubmit(onSubmit)}
                          >
                            {t("Sign up")}
                          </Button>
                        </>
                      )}

                      <Divider sx={{ bgcolor: "grey.300", mt: 2, mb: 4 }} />

                      {/* <div className="flex justify-center mb-4 mt-4">
                        <GoogleLogin onSuccess={handleGoogleSuccess} />
                      </div> */}

                      <Grid container direction="column" alignItems="center">
                        <Grid item>
                          <Link
                            onClick={() => {
                              if (step > 1) {
                                setStep(step - 1);
                              } else {
                                navigate("/SignIn");
                              }
                            }}
                            component="button"
                            underline="none"
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontWeight: 600,
                              fontSize: "12px",
                              lineHeight: "100%",
                              letterSpacing: 0,
                            }}
                          >
                            {t("Back to Sign In")}
                          </Link>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Container>
              </div>
            </div>
          </div>
        ) : (
          <NotAuthorisedPage />
        )}
      </div>

      {/* ERROR TOAST */}
      <Snackbar
        open={errorToast.open}
        autoHideDuration={4000}
        onClose={() => setErrorToast({ ...errorToast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        ContentProps={{
          sx: {
            backgroundColor: "#d32f2f",
            color: "white",
            fontWeight: "bold",
            fontSize: "14px",
          },
        }}
        message={errorToast.message}
      />
    </ThemeProvider>
  );
}
